// Shared helper functions for order-related components

// Format date as dd/mm/yy
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d)) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

// Format date with time as dd/mm/yy HH:MM
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d)) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}`;
};

// Format currency to Indian-style grouped number with 2 decimal places (₹1,23,456.78)
export const formatAmount = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '0.00';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Format square-cm / area values as rounded integer with Indian-style grouping (1,23,456)
export const formatArea = (value) => {
  if (value === null || value === undefined || value === '') return '0';
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-IN');
};

export const PRODUCTION_STAGES = [
  'Base Shell Procurement',
  'MS Grinding',
  'Plating',
  'Lathe',
  'Polish',
  'Planning',
  'Engraving',
  'Chroming',
  'Proofing',
  'Dispatch',
];

export const getChildCustomerName = (order, customers) => {
  const customer = customers.find(c => c.id === order?.customer_id);
  if (customer?.customer_name) return customer.customer_name;
  if (customer?.addresses?.length > 0 && customer.addresses[0].customer_name_child) {
    return customer.addresses[0].customer_name_child;
  }
  if (customer?.parent_customer_name) return customer.parent_customer_name;
  return order?.parent_customer_name || order?.customer_key || '-';
};

export const getKeyWayForOrder = (order, customers) => {
  if (order?.specifications?.key_way) return order.specifications.key_way;
  const customer = customers.find(c => c.id === order?.customer_id);
  if (customer?.bore_details?.length > 0) {
    const bore = order?.specifications?.bore;
    const match = customer.bore_details.find(bd => bd.outer_diameter === bore);
    if (match?.key_way) return match.key_way;
    if (customer.bore_details[0]?.key_way) return customer.bore_details[0].key_way;
  }
  return '-';
};

export const getStageStatus = (orderId, stageName, orderStages) => {
  const stages = orderStages[orderId] || [];
  const stage = stages.find(s => s.stage_name === stageName);
  return stage?.status || 'pending';
};

export const getStageDetails = (orderId, stageName, orderStages) => {
  const stages = orderStages[orderId] || [];
  return stages.find(s => s.stage_name === stageName);
};

// Build a computed job name from order spec fields.
// variant 'date'  -> "BRAND PRODUCT QTY DD/MM/YY"  (used in print outputs of Production Schedule, Invoice, BS Order)
// variant 'specs' -> "BRAND PRODUCT QTY C{circ} L{len} N{nos}"  (used on the cylinder label)
// Falls back to existing order.job_name if no spec fields are present.
export const getComputedJobName = (order, variant = 'date') => {
  if (!order) return '-';
  const s = order.specifications || {};
  const parts = [];
  if (s.brand_name) parts.push(String(s.brand_name).trim());
  if (s.product_name) parts.push(String(s.product_name).trim());
  if (s.qty) parts.push(String(s.qty).trim());

  if (variant === 'specs') {
    const c = s.circumference;
    const l = s.length;
    const n = s.nos_of_cylinders;
    if (c) parts.push(`C${c}`);
    if (l) parts.push(`L${l}`);
    if (n) parts.push(`N${n}`);
  } else if (variant === 'short') {
    // no extra suffix — just Brand Product Qty
  } else {
    if (s.packed_date) {
      const d = new Date(s.packed_date);
      if (!isNaN(d)) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        parts.push(`${dd}/${mm}/${yy}`);
      }
    }
  }

  if (parts.length === 0) return order.job_name || '-';
  return parts.join(' ');
};

export const getStatusBadge = (status) => {
  switch (status) {
    case 'completed': return 'D';
    case 'in_progress': return 'P';
    default: return '-';
  }
};

export const getOverallProgress = (orderId, orderStages, order = null) => {
  // Dispatch is derived on the backend as: (invoice exists) OR (legacy
  // production_stages.Dispatch=completed). See `dispatch_completed_at` on the
  // order. Denominator = all PRODUCTION_STAGES (10). Numerator = completed
  // non-Dispatch stages + 1 if Dispatch is done.
  const stages = (orderStages[orderId] || []).filter(s => s.stage_name !== 'Order Received' && s.stage_name !== 'Dispatch');
  const completedStages = stages.filter(s => s.status === 'completed').length;
  const dispatchDone = !!order?.dispatch_completed_at ? 1 : 0;
  return `${completedStages + dispatchDone}/${PRODUCTION_STAGES.length}`;
};
